import buildQuery from 'odata-query'
import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  DraftLead,
  IPendingLeadAutoReviewInput,
  IPendingLeadAutoReviewResult,
} from '@user/store/draftLeads/typings'
import { APIError, APIReject, get, post, remove } from '../services/crud'
import { ODataResponse } from '@sales-center/common'
import { ServiceConfig } from '../services/config'
import { StoreData } from '../reducers/typings'

const baseUrl = `${ServiceConfig.baseAPIUrl}${ServiceConfig.draftLead.draftLead}`
const odataUrl = `${ServiceConfig.baseAPIUrl}${ServiceConfig.draftLead.odata}`
const odataExportUrl = `${ServiceConfig.baseAPIUrl}${ServiceConfig.draftLead.odataExport}`

export const getDraftListExport = createAsyncThunk<
  string,
  string[],
  { rejectValue: APIError }
>('draftLead/export', async (columns, thunkApi) => {
  const {
    draftLeads: {
      filterSet: { normalizedFilters, orderBy },
      mode,
    },
  } = thunkApi.getState() as StoreData

  const qstring = buildQuery<DraftLead>({
    skip: 0,
    top: 0,
    orderBy,
    expand: mode === 'list' ? ['odataCustomFieldValues'] : [],
    filter: normalizedFilters,
  })

  const response = await get(
    `${odataExportUrl}${qstring}&selectedColumns=${columns}`,
  )

  if (response.ok) {
    return await (response as Response).text()
  }

  return thunkApi.rejectWithValue(await response.json())
})

export const getDraftLeadList = createAsyncThunk<
  ODataResponse<DraftLead>,
  void,
  { rejectValue: APIError }
>('draftLead/cards', async (_, thunkApi) => {
  const {
    draftLeads: {
      mode,
      page,
      perPage,
      filterSet: { normalizedFilters, orderBy },
    },
  } = thunkApi.getState() as StoreData

  const qstring = buildQuery<DraftLead>({
    count: page === 1,
    skip: (page - 1) * perPage,
    top: perPage,
    orderBy,
    expand: mode === 'list' ? ['odataCustomFieldValues'] : [],
    filter: normalizedFilters,
  })
  const response = await get(`${odataUrl}${qstring}`)

  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

//#region bulk actions
export const bulkDeleteDraftLeads = createAsyncThunk<
  void,
  number[],
  { rejectValue: APIError }
>('draftLead/bulk/delete', async (ids, thunkApi) => {
  const response = await remove(`${baseUrl}/bulkDelete`, ids)
  if (!response.ok) return thunkApi.rejectWithValue(await response.json())
})
//#endregion

export const deleteDraftLead = createAsyncThunk<
  void,
  number,
  { rejectValue: APIError }
>('draftLead/delete', async (id, thunkApi) => {
  const response = await remove(`${baseUrl}/${id}`, id)
  if (!response.ok) return thunkApi.rejectWithValue(await response.json())
})

export const saveAndAutoReviewDraftLead = createAsyncThunk<
  IPendingLeadAutoReviewResult,
  { id: number; input: IPendingLeadAutoReviewInput },
  { rejectValue: APIReject }
>('draftLead/autoReview', async ({ id, input }, thunkApi) => {
  const response = await post(
    `${baseUrl}/${id}${ServiceConfig.draftLead.autoReview}`,
    input,
  )
  if (!response.ok) return thunkApi.rejectWithValue(await response.json())
  return await response.json()
})
